/* eslint-disable react-refresh/only-export-components */

// Install a lightweight fetch guard early to reduce noisy Supabase 404s
import './lib/supabaseFetchGuard';

import React from 'react';
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "@fontsource/space-grotesk/400.css";
import "@fontsource/space-grotesk/500.css";
import "@fontsource/space-grotesk/600.css";
import "@fontsource/space-grotesk/700.css";
import "@fontsource/press-start-2p";
import "./index.css";
import { AudioProvider } from "./audio";
import { validateEnvironment } from "./lib/env";

// Capture window-level errors in debug mode for E2E diagnosis.
// Production builds drop console.*, and some ReferenceErrors may not include stacks.
try {
  if (import.meta.env.DEV || window.location.hash.includes('debug=1')) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    window.addEventListener('error', (event) => {
      w.__E2E_LAST_WINDOW_ERROR__ = {
        type: 'error',
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      };
    });
    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason;
      w.__E2E_LAST_WINDOW_ERROR__ = {
        type: 'unhandledrejection',
        message: (reason && typeof reason === 'object' && 'message' in reason ? (reason as { message?: string }).message : null) ||
          (typeof reason === 'string' ? reason : String(reason)),
      };
    });
  }
} catch {
  // ignore
}

// Ensure PWA updates don't leave the app in a broken state with stale cached chunks
// Loaded lazily so initial JS doesn't always pay the SW registration cost.
if (import.meta.env.PROD) {
  import('virtual:pwa-register').then(async ({ registerSW }) => {
    // Register the service worker and ensure the UI receives a callable update function
    const updateSW = registerSW({
      onNeedRefresh() {
        // When a new service worker is waiting, dispatch an event with a safe activate callback.
        try {
          window.dispatchEvent(
            new CustomEvent('ogs:pwa_need_refresh', {
              detail: {
                activate: async () => {
                  try {
                    // Ask the SW to skipWaiting and optionally reload when ready
                    await updateSW(true);
                  } catch (e) {
                    // Fallback: force reload
                    try { window.location.reload(); } catch { /* ignore */ }
                  }
                },
              },
            })
          );
        } catch {
          try { window.location.reload(); } catch { /* ignore */ }
        }
      },
    });
    // Start background version checking (periodic) to detect server-side deploys
    try {
      const mod = await import('./lib/updateChecker');
      if (mod && typeof mod.startUpdateChecker === 'function') {
        mod.startUpdateChecker();
      }
    } catch (e) {
      // ignore failures to load update checker
    }

    // Last-resort: if a hashed asset fails to load (404), clear SW caches and hard-reload.
    // This protects production from partial deploys or stale cached HTML.
    const recoverFromAssetFailure = async () => {
      try {
        const key = 'pwa_asset_recovery_attempted_v1';
        if (sessionStorage.getItem(key) === '1') return;
        sessionStorage.setItem(key, '1');
      } catch {
        // ignore
      }

      try {
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister()));
        }
        if ('caches' in window) {
          const names = await caches.keys();
          await Promise.all(names.map((n) => caches.delete(n)));
        }
      } catch {
        // ignore
      }
      // Use location.reload() (no forced-cache bust available reliably across browsers)
      // and rely on SW unregister + cache delete to fetch fresh assets.
      window.location.reload();
    };

    window.addEventListener(
      'error',
      (event) => {
        const target = event.target as EventTarget | null;
        if (!target) return;

        const href = target instanceof HTMLLinkElement ? target.href : null;
        const src = target instanceof HTMLScriptElement ? target.src : null;
        const url = href ?? src;
        if (!url) return;

        // Only react to hashed build assets.
        if (url.includes('/assets/') || /\/assets\//.test(url)) {
          recoverFromAssetFailure();
        }
      },
      true
    );
  });
}

// Validate environment configuration (development only)
if (import.meta.env.DEV) {
  validateEnvironment();

  // Dev safety: if a service worker was previously registered on this origin
  // (e.g., after running a production build locally), it can serve a stale app shell
  // and make new routes appear as 404. Unregister it and clear caches once per session.
  try {
    const key = 'dev_sw_cleanup_v1';
    if (sessionStorage.getItem(key) !== '1') {
      sessionStorage.setItem(key, '1');
      (async () => {
        try {
          if ('serviceWorker' in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map((r) => r.unregister()));
          }
        } catch {}

        try {
          if ('caches' in window) {
            const names = await caches.keys();
            await Promise.all(names.map((n) => caches.delete(n)));
          }
        } catch {}
      })();
    }
  } catch {
    // ignore
  }
}

// Set a safe `--vh` CSS variable to avoid mobile browser address-bar resizing issues.
// Uses the common pattern: --vh = 1% of the window.innerHeight.
const setSafeVh = () => {
  try {
    document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
  } catch {}
};
setSafeVh();
window.addEventListener('resize', setSafeVh);

// During development, unregister any service workers and clear caches to avoid
// stale production service workers serving outdated bundles locally.
if (import.meta.env.DEV) {
  try {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((regs) => regs.forEach((r) => r.unregister())).catch(() => {});
    }
    if ('caches' in window) {
      caches.keys().then((names) => names.forEach((n) => caches.delete(n))).catch(() => {});
    }
  } catch {
    // ignore failures
  }
}

const mount = (node: HTMLElement, element: React.ReactElement) => createRoot(node).render(element);

const rootEl = document.getElementById("root")!;

// Async component for optional GamiProvider
const AsyncGamiProvider = () => {
  const [GamiProvider, setGamiProvider] = React.useState<React.ComponentType<{ children: React.ReactNode }> | null>(null);

  React.useEffect(() => {
    import('./features/gamification/GamiProvider')
      .then(({ GamiProvider }) => setGamiProvider(() => GamiProvider))
      .catch(() => {
        if (import.meta.env.DEV) {
          console.warn('Failed to load GamiProvider');
        }
      });
  }, []);

  if (!GamiProvider) return <App />;
  return <GamiProvider><App /></GamiProvider>;
};

// Wrap with AudioProvider first, then optional GamiProvider
const AppWithProviders = (
  <AudioProvider>
    {import.meta.env.VITE_GAMIFICATION === 'true' ? (
      <AsyncGamiProvider />
    ) : (
      <App />
    )}
  </AudioProvider>
);

mount(rootEl, AppWithProviders);

document.addEventListener("touchstart", () => {
  const ctx = (window as Window & { __audioContext?: AudioContext }).__audioContext;
  if (ctx?.state === "suspended") ctx.resume();
}, { once: true });
